import React, { useEffect, useRef, useState } from 'react'
import WorkplaceBasicsInfo from './WorkplaceBasicsInfo'
import { WorkplaceQuizDetailProvider } from './context/WorkplaceQuizContext'
import useErrorHandler from '../../hooks/useErrorHandler'
import WorkplaceQuizModule from './WorkplaceQuizModule'
import WorkplaceQuizes from './WorkplaceQuizes'
import {
  IQuizView,
  IWorkplaceQuizModuleState,
  IWorkplaceQuizResponse,
  IWorkplaceQuizState,
} from '../../models/WorkplaceQuiz'
import Conditional from '../common/Conditional'
import WorkplaceQuizService from '../../services/WorkplaceQuizService'
import FormErrorDisplay from '../common/FormErrorMessages'
import axios, { CancelTokenSource } from 'axios'
import WorkplaceResultsResources from './WorkplaceResultsResources'

const WorkplaceBasicsView = () => {
  const mountedRef = useRef(false)
  const [isLoading, setIsLoading] = useState(true)
  const [userErrors, , handleErrors, ,] = useErrorHandler()
  const [state, setState] = useState<IWorkplaceQuizModuleState>()
  const [toggleMenuState, setToggleMenuState] = useState(false)
  const [quiz, setQuiz] = useState<IQuizView>()
  const { getQuizModules } = WorkplaceQuizService

  const toggleMenu = () => {
    setToggleMenuState((state) => !state)
  }

  const getModules = async (source: CancelTokenSource) => {
    return await getQuizModules(source).then((response) => {
      return response.result
    })
  }

  useEffect(() => {
    mountedRef.current = true
    const source = axios.CancelToken.source()

    setQuiz((prevState) => ({
      ...prevState,
      quizId: 0,
      showQuiz: false,
      showHome: true,
      showResult: false,
    }))

    getModules(source)
      .then((response: IWorkplaceQuizResponse) => {
        if (mountedRef.current) {
          const moduleState: IWorkplaceQuizModuleState = {
            header: response.introHeader,
            body: response.introBody,
            quizStatus: [],
            answers: [],
            glossaries: response.glossaries,
          }

          if (response.quizzes) {
            let index = 0
            while (index < response.quizzes.length) {
              const quiz = response.quizzes[index]
              const item: IWorkplaceQuizState = {
                quizId: quiz.id,
                title: quiz.title,
                completed: false,
                failed: false,
                totalQuestions: quiz.totalQuestions,
                correctAnswered: [],
                wrongAnswered: [],
                glossaries: [],
                failedResources: [],
                remainderResources: quiz.remainderResources || [],
              }
              moduleState.quizStatus.push(item)
              index++
            }
          }

          setState(moduleState)
        }
      })
      .catch((error) => {
        handleErrors(error)
      })
      .finally(() => {
        if (mountedRef.current) {
          setIsLoading(false)
        }
        window.scrollTo(0, 0)
      })

    return () => {
      mountedRef.current = false
      if (source) {
        source.cancel('Operation cancelled')
      }
    }
  }, [])

  const hamburger: HTMLElement | null = document.querySelector('#quickMenu')

  const startQuiz = (id: number) => {
    setQuiz((prevState) => ({
      ...prevState,
      quizId: id,
      showQuiz: id >= 0,
      showHome: id <= 0,
      showResult: false,
    }))
    setToggleMenuState(false)

    setTimeout(
      () => window.scrollTo({ left: 0, top: 0, behavior: 'smooth' }),
      350
    )
    hamburger?.focus()
  }

  const viewResult = () => {
    setQuiz((prevState) => ({
      ...prevState,
      quizId: 0,
      showQuiz: false,
      showHome: false,
      showResult: true,
    }))
    setToggleMenuState(false)

    setTimeout(
      () => window.scrollTo({ left: 0, top: 0, behavior: 'smooth' }),
      350
    )
    hamburger?.focus()
  }

  return (
    <Conditional if={!isLoading}>
      <FormErrorDisplay
        heading="Please review the following errors"
        show={userErrors?.length > 0}
        errorsList={userErrors}
      />

      <WorkplaceBasicsInfo />
      <div id="app">
        <div className="layout">
          <WorkplaceQuizDetailProvider currentState={state}>
            <Conditional if={quiz?.quizId <= 0 && quiz.showHome}>
              <WorkplaceQuizes
                isOpen={toggleMenuState}
                menuToggled={toggleMenu}
                startQuiz={startQuiz}
                viewResult={viewResult}
              />
            </Conditional>
            <Conditional if={quiz?.quizId > 0 && quiz.showQuiz}>
              <WorkplaceQuizModule
                quizId={quiz?.quizId}
                isOpen={toggleMenuState}
                menuToggle={toggleMenu}
                startQuiz={startQuiz}
                viewResults={viewResult}
                handleErrors={handleErrors}
              />
            </Conditional>
            <Conditional if={quiz?.showResult}>
              <WorkplaceResultsResources
                isOpen={toggleMenuState}
                menuToggled={toggleMenu}
                startQuiz={startQuiz}
                viewResult={viewResult}
              />
            </Conditional>
          </WorkplaceQuizDetailProvider>
        </div>
      </div>
    </Conditional>
  )
}

export default WorkplaceBasicsView
